/**
 * Analytics Utility Functions
 */

import type { DateRange, Forecast, SalesDataPoint } from './types';

// Generate month options from June 2025 to current month
export function generateMonthOptions(): DateRange[] {
 const options: DateRange[] = [];
 const startDate = new Date(2025, 5, 1); // June 2025
 const now = new Date();
 const currentMonth = new Date(now.getFullYear(), now.getMonth(), 1);
 const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

 options.push({ label: 'All Time', value: 'all', startDate: null, endDate: null });

 let date = new Date(startDate);
 while (date <= currentMonth) {
 const year = date.getFullYear();
 const month = date.getMonth();
 const monthStart = new Date(Date.UTC(year, month, 1, 0, 0, 0, 0));
 const monthEnd = new Date(Date.UTC(year, month + 1, 0, 23, 59, 59, 999));

 options.push({
 label: `${monthNames[month]} ${year}`,
 value: `${year}-${String(month + 1).padStart(2, '0')}`,
 startDate: monthStart,
 endDate: monthEnd
 });
 date = new Date(year, month + 1, 1);
 }
 return options;
}

// Get current month value
export function getCurrentMonthValue(): string {
 const now = new Date();
 return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
}

// Get previous period date range
export function getPreviousPeriod(startDate: Date | null, endDate: Date | null): { start: Date; end: Date } | null {
 if (!startDate || !endDate) return null;
 const duration = endDate.getTime() - startDate.getTime();
 return {
 start: new Date(startDate.getTime() - duration - 1),
 end: new Date(startDate.getTime() - 1)
 };
}

// Calculate growth percentage
export function calcGrowth(current: number, previous: number): number {
 if (previous === 0) return current > 0 ? 100 : 0;
 return ((current - previous) / previous) * 100;
}

// Format currency
export function formatCurrency(value: number, decimals = 0): string {
 return '$' + value.toLocaleString(undefined, { maximumFractionDigits: decimals });
}

// Format percentage
export function formatPercent(value: number): string {
 const sign = value >= 0 ? '+' : '';
 return `${sign}${value.toFixed(1)}%`;
}

// Calculate median
export function median(values: number[]): number {
 if (values.length === 0) return 0;
 const sorted = [...values].sort((a, b) => a - b);
 const mid = Math.floor(sorted.length / 2);
 return sorted.length % 2 ? sorted[mid] : (sorted[mid - 1] + sorted[mid]) / 2;
}

// Get week number
export function getWeekNumber(date: Date): number {
 const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
 const dayNum = d.getUTCDay() || 7;
 d.setUTCDate(d.getUTCDate() + 4 - dayNum);
 const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
 return Math.ceil((((d.getTime() - yearStart.getTime()) / 86400000) + 1) / 7);
}

// Calculate forecast based on current data
export function calculateForecast(
 data: SalesDataPoint[],
 currentRevenue: number,
 currentShares: number,
 endDate: Date | null
): Forecast {
 const now = new Date();
 const monthEnd = endDate || new Date(now.getFullYear(), now.getMonth() + 1, 0);
 const daysRemaining = Math.max(0, Math.ceil((monthEnd.getTime() - now.getTime()) / 86400000));
 const daysPassed = data.length || 1;
 const dailyAvgRevenue = currentRevenue / daysPassed;
 const dailyAvgShares = currentShares / daysPassed;

 return {
 projectedRevenue: currentRevenue + (dailyAvgRevenue * daysRemaining),
 projectedShares: currentShares + (dailyAvgShares * daysRemaining),
 daysRemaining,
 dailyAverage: dailyAvgRevenue,
 confidence: daysPassed < 7 ? 'low' : daysPassed < 14 ? 'medium' : 'high'
 };
}

// Generate sparkline data (last 7 data points)
export function generateSparklineData(data: SalesDataPoint[], metric: keyof SalesDataPoint): number[] {
 return data.slice(-7).map(d => (typeof d[metric] === 'number' ? d[metric] as number : 0));
}

// Quick date presets
export function getDatePresets(): DateRange[] {
 const now = new Date();
 return [
 { label: 'Last 7 days', value: 'last7', startDate: new Date(now.getTime() - 7 * 86400000), endDate: now },
 { label: 'Last 30 days', value: 'last30', startDate: new Date(now.getTime() - 30 * 86400000), endDate: now },
 { label: 'This Quarter', value: 'quarter', startDate: new Date(now.getFullYear(), Math.floor(now.getMonth() / 3) * 3, 1), endDate: now },
 { label: 'This Year', value: 'year', startDate: new Date(now.getFullYear(), 0, 1), endDate: now },
 ];
}

// Export data to CSV
export function exportToCSV(data: SalesDataPoint[], filename: string): void {
 const headers = ['Date', 'Revenue', 'Shares', 'Bank Revenue', 'Crypto Revenue', 'Commission Shares', 'Converted Shares'];
 const rows = data.map(d => [d.label, d.revenue, d.shares, d.bankRevenue || 0, d.cryptoRevenue || 0, d.commissionShares, d.convertedShares]);
 const csv = [headers.join(','), ...rows.map(r => r.join(','))].join('\n');
 const blob = new Blob([csv], { type: 'text/csv' });
 const url = URL.createObjectURL(blob);
 const a = document.createElement('a');
 a.href = url;
 a.download = `${filename}.csv`;
 a.click();
 URL.revokeObjectURL(url);
}

